import {
  type ConsentList,
  encodePhotoUploads,
  type FileRef,
  MAX_PDF_PAGES,
  MAX_PHOTO_PAGES,
  type UploadWidget,
} from '@oneapp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../components/icons';
import { Sheet } from '../components/Sheet';
import { Button, Callout } from '../components/ui';
import { ApiError } from '../lib/api';
import { aiPhotosAllowed, privacyApi, privacyKeys } from '../privacy/api';
import { AiPhotosText } from '../privacy/parts';
import { importsApi } from './api';
import { type PreparedFile, PrepareError, type PrepareProblem, prepareFile } from './photos';

/**
 * Phase 8 · Add photos of your invoices: take photos or choose them (and PDFs), see them, send them.
 * Each is made ready on the phone first (a JPEG of the right size, the right way up, without its
 * location; a PDF's pages as pictures), then goes straight to storage through a short-lived link and is
 * checked by the server (a picture inside, the malware scan) before the chat has them read.
 */

interface Item {
  key: string;
  file: PreparedFile;
  /** A preview for each page. */
  previews: string[];
}

type Refused = { name: string; why: PrepareProblem | 'too_many' | 'size' };

type Stage =
  | { kind: 'idle' }
  | { kind: 'preparing' }
  | { kind: 'sending'; done: number; total: number }
  | { kind: 'failed'; why: 'wrong_type' | 'infected' | 'scan_failed' | 'upload'; photo: number };

/** The server's reasons for refusing a photo, as what the sheet says. */
const FAILURES: Record<string, 'wrong_type' | 'infected' | 'scan_failed'> = {
  wrong_type: 'wrong_type',
  infected: 'infected',
  scan_failed: 'scan_failed',
};

let keys = 0;

interface PhotoSheetProps {
  widget: UploadWidget;
  onClose: () => void;
  /** The photos are up and passed the checks: the chat takes them from here. */
  onUploaded: (uploadIds: string, files: FileRef[]) => void;
}

/**
 * The photo page, after asking once whether photos may be read by AI (D11, Phase 17): they're read by
 * OpenAI, in the USA. The server checks it too, before reading any.
 */
export function PhotoSheet(props: PhotoSheetProps) {
  const { t } = useTranslation();
  const consents = useQuery({
    queryKey: privacyKeys.consents,
    queryFn: ({ signal }) => privacyApi.consents(signal),
    staleTime: 30_000,
  });
  const label = props.widget.secureLabel ?? t('chat.widget.secure');
  if (consents.isPending) {
    return (
      <Sheet open onClose={props.onClose} label={label}>
        <p className="sheet__text muted" role="status">
          {t('app.loading')}
        </p>
      </Sheet>
    );
  }
  // Offline: the picker opens, and the server asks when the photos are sent.
  if (consents.data && !aiPhotosAllowed(consents.data)) return <PhotosConsent label={label} onClose={props.onClose} />;
  return <PhotoPicker {...props} />;
}

/** D11 · Before I read your photos: Agree and add photos, or Not now. */
function PhotosConsent({ label, onClose }: { label: string; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const agree = useMutation({
    mutationFn: () => privacyApi.agree('ai_photos'),
    onSuccess: async ({ consent }) => {
      queryClient.setQueryData<ConsentList>(privacyKeys.consents, (list) =>
        list
          ? {
              ...list,
              consents: [
                ...list.consents.filter((c) => !(c.type === consent.type && c.version === consent.version)),
                consent,
              ],
            }
          : list,
      );
      await queryClient.invalidateQueries({ queryKey: privacyKeys.consents });
    },
  });
  return (
    <Sheet
      open
      onClose={onClose}
      label={label}
      footer={
        <div className="sheet__buttons sheet__buttons--stack">
          <Button busy={agree.isPending} onClick={() => agree.mutate()}>
            {t('photos.consentAgree')}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            {t('photos.consentLater')}
          </Button>
        </div>
      }
    >
      <h2 className="sheet__title">{t('photos.consentTitle')}</h2>
      <AiPhotosText />
      {agree.isError ? (
        <Callout tone="error" icon="alert-triangle" role="alert">
          {t('errors.generic')}
        </Callout>
      ) : null}
    </Sheet>
  );
}

function PhotoPicker({ widget, onClose, onUploaded }: PhotoSheetProps) {
  const { t } = useTranslation();
  const chooseId = useId();
  const cameraId = useId();
  const choose = useRef<HTMLInputElement>(null);
  const camera = useRef<HTMLInputElement>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [refused, setRefused] = useState<Refused[]>([]);
  const [stage, setStage] = useState<Stage>({ kind: 'idle' });
  const busy = stage.kind === 'preparing' || stage.kind === 'sending';
  const maxBytes = (widget.maxSizeMb ?? 25) * 1024 * 1024;
  const pages = items.reduce((n, item) => n + item.file.pages.length, 0);

  // Previews are let go when the sheet closes.
  const previews = useRef<string[]>([]);
  previews.current = items.flatMap((item) => item.previews);
  useEffect(
    () => () => {
      for (const url of previews.current) URL.revokeObjectURL(url);
    },
    [],
  );

  const add = async (list: FileList | null) => {
    if (!list?.length) return;
    const files = [...list];
    setStage({ kind: 'preparing' });
    const found: Refused[] = [];
    const added: Item[] = [];
    let count = pages;
    for (const file of files) {
      if (file.size > maxBytes) {
        found.push({ name: file.name, why: 'size' });
        continue;
      }
      try {
        const prepared = await prepareFile(file);
        if (count + prepared.pages.length > MAX_PHOTO_PAGES) {
          found.push({ name: file.name, why: 'too_many' });
          continue;
        }
        count += prepared.pages.length;
        added.push({
          key: `photo-${++keys}`,
          file: prepared,
          previews: prepared.pages.map((page) => URL.createObjectURL(page.blob)),
        });
      } catch (error) {
        found.push({ name: file.name, why: error instanceof PrepareError ? error.problem : 'unreadable' });
      }
    }
    setItems((current) => [...current, ...added]);
    setRefused(found);
    setStage({ kind: 'idle' });
    if (choose.current) choose.current.value = '';
    if (camera.current) camera.current.value = '';
  };

  const remove = (key: string) => {
    setItems((current) => {
      const gone = current.find((item) => item.key === key);
      for (const url of gone?.previews ?? []) URL.revokeObjectURL(url);
      return current.filter((item) => item.key !== key);
    });
  };

  const send = async () => {
    const total = pages;
    let done = 0;
    let photo = 0;
    setRefused([]);
    setStage({ kind: 'sending', done, total });
    const entries: (string | string[])[] = [];
    const files: FileRef[] = [];
    try {
      for (const item of items) {
        const ids: string[] = [];
        for (const page of item.file.pages) {
          photo++;
          const ticket = await importsApi.createUpload({
            purpose: 'invoice_photo',
            fileName: page.name,
            contentType: 'image/jpeg',
            size: page.blob.size,
          });
          await importsApi.put(ticket, page.blob);
          const { upload } = await importsApi.complete(ticket.upload.id);
          if (upload.status === 'rejected') {
            setStage({ kind: 'failed', why: FAILURES[upload.rejectReason ?? ''] ?? 'upload', photo });
            return;
          }
          ids.push(upload.id);
          files.push({ id: upload.id, name: upload.fileName, size: upload.size, mime: upload.contentType });
          done++;
          setStage({ kind: 'sending', done, total });
        }
        entries.push(item.file.kind === 'pdf' && ids.length > 1 ? ids : (ids[0] as string));
      }
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      setStage({ kind: 'failed', why: 'upload', photo });
      return;
    }
    onUploaded(encodePhotoUploads(entries), files);
  };

  let number = 0;
  return (
    <Sheet
      open
      onClose={busy ? () => {} : onClose}
      label={widget.secureLabel ?? t('chat.widget.secure')}
      footer={
        <div className="sheet__buttons sheet__buttons--stack">
          <Button variant="secondary" icon="camera" disabled={busy} onClick={() => camera.current?.click()}>
            {t('imports.photos.take')}
          </Button>
          {items.length ? (
            <Button icon="check" busy={stage.kind === 'sending'} disabled={busy} onClick={send}>
              {pages === 1 ? t('imports.photos.readOne') : t('imports.photos.read', { count: pages })}
            </Button>
          ) : (
            <Button icon="image" busy={stage.kind === 'preparing'} onClick={() => choose.current?.click()}>
              {widget.cta}
            </Button>
          )}
        </div>
      }
    >
      <h2 className="sheet__title">{widget.title}</h2>
      {widget.warning ? (
        <Callout tone="amber" icon="flask">
          {widget.warning}
        </Callout>
      ) : null}
      {items.length ? (
        <ul className="photo-grid" aria-label={t('imports.photos.chosen', { count: pages })}>
          {items.map((item) =>
            item.previews.map((url, i) => {
              number++;
              const first = i === 0;
              return (
                <li key={`${item.key}-${url}`} className="photo-grid__item">
                  <img src={url} alt={t('imports.photos.alt', { number, name: item.file.pages[i]?.name ?? '' })} />
                  <span className="photo-grid__number" aria-hidden="true">
                    {number}
                  </span>
                  {item.file.kind === 'pdf' && first ? (
                    <span className="photo-grid__pdf">
                      {t('imports.photos.pdfPages', { count: item.file.pages.length })}
                    </span>
                  ) : null}
                  {first ? (
                    <button
                      type="button"
                      className="photo-grid__remove"
                      aria-label={t('imports.photos.remove', { name: item.file.name })}
                      disabled={busy}
                      onClick={() => remove(item.key)}
                    >
                      <Icon name="x" size={16} />
                    </button>
                  ) : null}
                </li>
              );
            }),
          )}
          {pages < MAX_PHOTO_PAGES ? (
            <li className="photo-grid__item photo-grid__item--add">
              <button type="button" disabled={busy} onClick={() => choose.current?.click()}>
                <Icon name="plus" size={22} />
                <span>{t('imports.photos.add')}</span>
              </button>
            </li>
          ) : null}
        </ul>
      ) : (
        <label htmlFor={chooseId} className={`dropzone${busy ? ' dropzone--busy' : ''}`}>
          <span className="icon-tile icon-tile--large">
            <Icon name="camera" size={25} />
          </span>
          <span className="dropzone__title">{t('imports.photos.choose')}</span>
          <span className="dropzone__hint">{t('imports.photos.limits', { count: MAX_PHOTO_PAGES })}</span>
        </label>
      )}
      <input
        ref={choose}
        id={chooseId}
        type="file"
        className="visually-hidden"
        accept="image/*,application/pdf"
        multiple
        disabled={busy}
        onChange={(event) => void add(event.target.files)}
      />
      <input
        ref={camera}
        id={cameraId}
        type="file"
        className="visually-hidden"
        accept="image/*"
        capture="environment"
        disabled={busy}
        aria-label={t('imports.photos.take')}
        onChange={(event) => void add(event.target.files)}
      />
      {stage.kind === 'preparing' ? (
        <p className="sheet__text muted" role="status">
          {t('imports.photos.preparing')}
        </p>
      ) : null}
      {stage.kind === 'sending' ? (
        <div className="photo-progress" role="status">
          <span>
            {t('imports.photos.sending', { done: Math.min(stage.done + 1, stage.total), total: stage.total })}
          </span>
          <span className="progress" aria-hidden="true">
            <i style={{ width: `${Math.round((stage.done / Math.max(1, stage.total)) * 100)}%` }} />
          </span>
        </div>
      ) : null}
      {stage.kind === 'failed' ? (
        <Callout tone="error" icon="alert-triangle" role="alert">
          {t(`imports.photos.failed.${stage.why}`, { number: stage.photo })}
        </Callout>
      ) : null}
      {refused.length ? (
        <Callout tone="error" icon="alert-triangle" role="alert">
          {refused.map((r) => (
            <span key={`${r.name}-${r.why}`} className="photo-refused">
              {t(`imports.photos.refused.${r.why}`, { name: r.name, max: MAX_PHOTO_PAGES, pages: MAX_PDF_PAGES })}
            </span>
          ))}
        </Callout>
      ) : null}
      {widget.note ? <p className="sheet__text muted">{widget.note}</p> : null}
      <p className="sheet__text muted small">{t('imports.photos.privacy')}</p>
    </Sheet>
  );
}
